import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { useTranslation } from 'react-i18next'
import { toast } from '@itsrever/design-system'
import DashboardUsersApi from '@/infrastructure/api/dashboard-users'
import EcommerceContactsApi from '@/infrastructure/api/ecommerce-contacts'
import {
  DashboardUser,
  DashboardUserPermissions
} from '@/domain/dashboard-user'
import {
  PointOfContact,
  mergePointOfContact,
  pointOfContactFor,
  samePointOfContact,
  withPointOfContact
} from '@/domain/ecommerce-contact'
import {
  contactsKey,
  useContactsGroup
} from '@/application/manage-ecommerce-contacts'
import { useAppSelector } from '@/redux/hooks'

// Prefix only: an invite invalidates every list, while each cached list is keyed by the group
// and ecommerce it belongs to. The list is a group's, and for REVER staff which group that is
// depends on the selected ecommerce — so an entry is only valid for that pair and must not be
// served to another one.
const DASHBOARD_USERS_KEY = ['dashboardUsers']

// One builder for the whole key, never a spread at the call site: reads and cache patches
// diverged exactly because the query appended the ecommerce and the patches did not, so the
// writes landed on an entry nothing read.
function usersKey(group?: string, ecommerceId?: string) {
  return [...DASHBOARD_USERS_KEY, group, ecommerceId]
}

// Writes patch the cached row instead of refetching. Auth0 applies the write to the user record
// immediately but lists users through its search index, which lags by seconds — so a refetch
// fired right after a successful write returns the value we just replaced. The response is
// already proof the server took what we sent, so the cache can hold it and the next natural
// refetch (remount, window focus) reconciles.
function patchCachedUser(
  users: DashboardUser[] | undefined,
  id: string,
  change: Partial<DashboardUser>
) {
  return users?.map((user) => (user.id === id ? { ...user, ...change } : user))
}

// enabled keeps a non-admin who reaches the page by URL from firing a request the API will
// refuse: the tab is admin-only, and a 403 in the console is not a useful way to say so.
export function useDashboardUsers({
  enabled,
  ecommerceId
}: {
  enabled: boolean
  ecommerceId?: string
}) {
  const group = useAppSelector((s) => s.userApi.getMe.response.user?.group)
  const query = useQuery({
    queryKey: usersKey(group, ecommerceId),
    queryFn: ({ signal }) =>
      DashboardUsersApi.getDashboardUsers(ecommerceId, signal),
    enabled
  })

  return {
    users: query.data ?? [],
    loadingUsers: query.isLoading,
    usersError: query.isError,
    reloadUsers: query.refetch
  }
}

type SaveDashboardUserInput = DashboardUserPermissions & {
  id?: string
  email: string
  pointOfContact: PointOfContact
}

// Inviting and editing share a mutation because they are the same dialog: an id is what tells
// them apart, and only an existing user has one.
export function useSaveDashboardUser(ecommerceId?: string) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const group = useAppSelector((s) => s.userApi.getMe.response.user?.group)
  const contactsGroup = useContactsGroup(ecommerceId)

  const mutation = useMutation({
    mutationFn: async (input: SaveDashboardUserInput) => {
      const permissions: DashboardUserPermissions = {
        role: input.role,
        assignedWarehouses: input.assignedWarehouses
      }
      if (input.id) {
        await DashboardUsersApi.updateDashboardUser(
          input.id,
          permissions,
          ecommerceId
        )
      } else {
        if (!ecommerceId) throw new Error('missing ecommerce')
        await DashboardUsersApi.inviteDashboardUser(
          ecommerceId,
          input.email,
          permissions
        )
      }
      return {
        pointOfContactSaved: await savePointOfContact(input, contactsGroup)
      }
    },
    onSuccess: ({ pointOfContactSaved }, input) => {
      queryClient.invalidateQueries({ queryKey: contactsKey(contactsGroup) })
      toast(
        pointOfContactSaved
          ? {
              text: input.id
                ? t('dashboard_users.update_success')
                : t('dashboard_users.invite_success'),
              variant: 'success'
            }
          : {
              text: t('dashboard_users.point_of_contact_error'),
              variant: 'error'
            }
      )
      if (input.id) {
        const id = input.id
        queryClient.setQueryData(
          usersKey(group, ecommerceId),
          (users?: DashboardUser[]) =>
            patchCachedUser(users, id, {
              role: input.role,
              assignedWarehouses: input.assignedWarehouses
            })
        )
        return
      }
      // An invite has no id to patch with — Auth0 mints it — so the new row can only come from
      // a refetch, and shows up once the search index catches up.
      queryClient.invalidateQueries({ queryKey: DASHBOARD_USERS_KEY })
    },
    onError: (error, input) => {
      console.warn(error)
      toast({ text: t(saveErrorKey(error, input.id)), variant: 'error' })
    }
  })

  return { saveUser: mutation.mutate, savingUser: mutation.isPending }
}

export function useSetDashboardUserAccess(ecommerceId?: string) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const group = useAppSelector((s) => s.userApi.getMe.response.user?.group)

  const mutation = useMutation({
    mutationFn: (input: { id: string; revoked: boolean }) =>
      DashboardUsersApi.setDashboardUserAccessRevoked(
        input.id,
        input.revoked,
        ecommerceId
      ),
    onSuccess: (_, input) => {
      toast({
        text: input.revoked
          ? t('dashboard_users.revoke_success')
          : t('dashboard_users.restore_success'),
        variant: 'success'
      })
      queryClient.setQueryData(
        usersKey(group, ecommerceId),
        (users?: DashboardUser[]) =>
          patchCachedUser(users, input.id, { accessRevoked: input.revoked })
      )
    },
    onError: (error) => {
      console.warn(error)
      toast({ text: t('dashboard_users.access_error'), variant: 'error' })
    }
  })

  return {
    setUserAccess: mutation.mutate,
    changingAccess: mutation.isPending
  }
}

// Reported, not thrown: the user is already saved, and a retry would invite them twice.
async function savePointOfContact(
  input: SaveDashboardUserInput,
  contactsGroup?: string
) {
  if (!contactsGroup) return true
  try {
    await EcommerceContactsApi.updateGroupContacts(
      contactsGroup,
      (contacts) => {
        const current = pointOfContactFor(contacts, input.email)
        // An invite never showed the email's stored topics, so it may only add to them.
        const wanted = input.id
          ? input.pointOfContact
          : mergePointOfContact(current, input.pointOfContact)
        if (samePointOfContact(current, wanted)) return undefined
        return withPointOfContact(contacts, input.email, wanted)
      }
    )
    return true
  } catch (error) {
    console.warn(error)
    return false
  }
}

// Auth0 keeps one account per email address, so inviting someone who already has a REVER account
// is the likeliest invite failure and deserves to say so rather than read as a generic error.
function saveErrorKey(error: unknown, id?: string) {
  if (id) return 'dashboard_users.update_error'
  if (isAxiosError(error) && error.response?.status === 409) {
    return 'dashboard_users.invite_exists_error'
  }
  return 'dashboard_users.invite_error'
}
